import * as Sentry from '@sentry/react'
import { Bug } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import { isSentryUserFeedbackAvailable } from '@/services/sentry'

export interface FeedbackTriggerProps {
	/** Sidebar: hide label when collapsed */
	collapsed?: boolean
	/** Bottombar: larger icon size and icon-only (no label) */
	iconSize?: number
	/** When true, only show icon (e.g. bottombar) */
	iconOnly?: boolean
	className?: string
}

/**
 * Renders a nav-style button with a bug icon that opens the Sentry user
 * feedback form. Use in sidebar (below Help) and bottombar. Renders nothing
 * when Sentry or user feedback is disabled.
 */
export function FeedbackTrigger({
	collapsed,
	iconSize,
	iconOnly,
	className,
}: FeedbackTriggerProps) {
	const buttonRef = useRef<HTMLButtonElement>(null)
	const { t } = useTranslation()

	useEffect(() => {
		const enabled = isSentryUserFeedbackAvailable()
		if (!enabled || !buttonRef.current) return

		const feedback = Sentry.getFeedback()
		const cleanup = feedback?.attachTo(buttonRef.current, {
			formTitle: t('sentryFeedback.formTitle'),
		})

		// Return the cleanup so React StrictMode (dev) can remove the
		// previous handler before re-attaching, preventing duplicate dialogs.
		return cleanup
	}, [t])

	const enabled = isSentryUserFeedbackAvailable()
	if (!enabled) return null

	return (
		<button
			ref={buttonRef}
			type="button"
			className={cn(
				'flex gap-2 p-2 rounded-lg truncate transition-all cursor-pointer active:scale-100 hover:scale-105 duration-300 ease-in-out hover:bg-gray-100 hover:text-gray-900',
				collapsed ? '' : 'pe-12',
				className
			)}
			title={collapsed || iconOnly ? t('navigation.reportBug') : undefined}
			aria-label={t('navigation.reportBug')}
		>
			<span className="backface-hidden">
				<Bug size={iconSize} />
			</span>
			{!collapsed && !iconOnly && t('navigation.reportBug')}
		</button>
	)
}
